// Where organisations stand in their setup (spec/14 §3.2, F-HQ-03): the Organisations list's Setup column — "Live",
// "No venue yet", or "2 of 4 · stuck at pairing a PC" — and a Setup panel on an organisation's page with each venue's
// essentials. One read, `GET /v1/hq/setup?organizationId=…`, for the page of organisations loaded (at most 100 a call).
//
// **Reading it is recorded** in every organisation it returns, as HQ's venue list is (spec/14 §3.3): it reaches into
// the organisation's venues, prices and stations. It needs `hq:organizations`, which the Organisations screens already
// require; the tracker says so in an info tip.

import type { HqOrganizationSetup, HqSetupNextStep } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, InfoTip, ProgressBar, Skeleton, StepTracker } from '@clavian/ui-web';
import { useQueries, useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { useApi } from '../../api/client';

/** The most organisations one read names (the contract's limit, a page of the list). */
const PER_READ = 100;

const setupKey = (ids: readonly string[]) => ['hq', 'setup', ids.join(',')] as const;

/** Each organisation's setup, by id, for the organisations loaded — read a page (100) at a time, as they load. */
export function useHqSetupOf(organizationIds: readonly string[]): ReadonlyMap<string, HqOrganizationSetup> | undefined {
  const api = useApi();
  const chunks: string[][] = [];
  for (let at = 0; at < organizationIds.length; at += PER_READ) chunks.push(organizationIds.slice(at, at + PER_READ));
  const results = useQueries({
    queries: chunks.map((ids) => ({
      queryKey: setupKey(ids),
      queryFn: () => api.listHqSetup({ query: { organizationId: ids.join(',') } }),
      // A read is recorded in each organisation: read it when HQ opens the list, not every time the window regains focus.
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      retry: false,
    })),
  });
  if (results.some((result) => result.data === undefined)) return results.some((result) => result.isError) ? new Map() : undefined;
  return new Map(results.flatMap((result) => result.data?.data ?? []).map((setup) => [setup.organizationId, setup]));
}

/** The words for a step HQ names: "pairing a PC", "opening hours". */
function useStepWords(): (step: HqSetupNextStep) => string {
  const { t } = useTranslation('hq');
  return (step) => t(`organizations.setup.step.${step}`);
}

/** The Setup column's cell: Live, No venue yet, or how far along and where it is stuck. */
export function SetupCell({ setup, loading }: { readonly setup: HqOrganizationSetup | undefined; readonly loading: boolean }): ReactNode {
  const { t } = useTranslation('hq');
  const words = useStepWords();
  if (setup === undefined) return <span className="console-subtle">{loading ? t('organizations.setup.loading') : t('organizations.setup.unavailable')}</span>;
  if (setup.state === 'live') {
    return (
      <Badge tone="ok" dot>
        {t('organizations.setup.live')}
      </Badge>
    );
  }
  const text =
    setup.nextStep === undefined
      ? t('organizations.setup.progress', { done: setup.essentialsDone, total: setup.essentialsTotal })
      : t('organizations.setup.stuck', { done: setup.essentialsDone, total: setup.essentialsTotal, step: words(setup.nextStep) });
  return <span className="console-hq-nowrap console-num">{setup.state === 'no_venue' && setup.nextStep === 'firstVenue' ? t('organizations.setup.noVenue') : text}</span>;
}

/** One organisation's setup: the same read the list makes, for its page. Reading it is recorded in the organisation's log. */
export function useHqOrganizationSetup(organizationId: string) {
  const api = useApi();
  const read = useQuery({
    queryKey: setupKey([organizationId]),
    queryFn: () => api.listHqSetup({ query: { organizationId } }),
    refetchOnWindowFocus: false,
    retry: false,
  });
  return { setup: read.data?.data[0], pending: read.isPending, error: read.isError };
}

const TRACKER_STEPS = ['firstVenue', 'openingHours', 'price', 'stationPaired'] as const;

/** The four essentials, done or not: the first venue, then the essentials of the venue nearest to ready. */
export function trackerStepsOf(setup: HqOrganizationSetup | undefined): readonly { readonly id: (typeof TRACKER_STEPS)[number]; readonly done: boolean }[] {
  const nearest = setup?.venues.reduce<HqOrganizationSetup['venues'][number] | undefined>(
    (best, venue) => (best === undefined || venue.essentialsDone > best.essentialsDone ? venue : best),
    undefined,
  );
  return TRACKER_STEPS.map((id) => ({
    id,
    done: setup === undefined ? false : setup.state === 'live' ? true : id === 'firstVenue' ? setup.venues.length > 0 : nearest !== undefined && !(nearest.missingEssentials as readonly string[]).includes(id),
  }));
}

/**
 * "Getting ready to open" (spec/14 §3.2): the four essentials as a segmented tracker, a sentence naming what is next,
 * and one button for the next action — which head office's `action` supplies, because what it can do depends on
 * whether the owner has joined — and under it each venue's essentials.
 */
export function OrganizationSetupTracker({ organizationId, action }: { readonly organizationId: string; readonly action?: ReactNode }): ReactNode {
  const { t } = useTranslation('hq');
  const words = useStepWords();
  const { setup, pending } = useHqOrganizationSetup(organizationId);
  if (setup === undefined) {
    return pending ? (
      <section className="console-hq-panel" aria-label={t('organizations.setup.title')} aria-busy="true">
        <Skeleton shape="text" width="12rem" />
        <Skeleton height="2.5rem" />
      </section>
    ) : null;
  }
  const steps = trackerStepsOf(setup);
  const venue = setup.venues.reduce<HqOrganizationSetup['venues'][number] | undefined>((best, v) => (best === undefined || v.essentialsDone > best.essentialsDone ? v : best), undefined);
  const next = setup.nextStep === undefined ? undefined : t(`organizations.setup.next.${setup.nextStep}`, { venue: venue?.name ?? '' });
  return (
    <StepTracker
        title={t('organizations.setup.title')}
        titleExtra={<InfoTip>{t('organizations.setup.intro')}</InfoTip>}
        steps={steps.map((step) => ({ id: step.id, label: t(`organizations.setup.trackerStep.${step.id}`), done: step.done }))}
        next={next}
        doneText={t('organizations.setup.allDone')}
        action={action}
        className="console-hq-tracker"
      >
        {setup.venues.length === 0 ? null : (
        <ul className="console-hq-setup-venues" aria-label={t('organizations.setup.venuesLabel')}>
          {setup.venues.map((v) => {
            const opened = v.status === 'live' || v.status === 'paused';
            return (
              <li key={v.venueId} className="console-hq-setup-venue">
                <span className="console-hq-setup-venue__name">{v.name}</span>
                <ProgressBar value={v.essentialsTotal === 0 ? 1 : v.essentialsDone / v.essentialsTotal} label={t('organizations.setup.venueEssentials', { done: v.essentialsDone, total: v.essentialsTotal })} tone="ok" size="sm" />
                <span className="console-subtle">
                  {opened
                    ? t('organizations.setup.venueOpen')
                    : v.missingEssentials.length === 0
                      ? t('organizations.setup.venueReady')
                      : `${t('organizations.setup.venueEssentials', { done: v.essentialsDone, total: v.essentialsTotal })} · ${t('organizations.setup.venueMissing', { steps: v.missingEssentials.map(words).join(', ') })}`}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </StepTracker>
  );
}
